import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, Building2, MapPin, User, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Tabs } from '@/components/Tabs';
import { extractErrorMessage } from '@/lib/api';
import type { Client, ClientInput, ClientStatus } from '@/lib/types';

interface ClientFormProps {
  initial?: Client | null;
  onSubmit: (input: ClientInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const STATUSES: ClientStatus[] = ['active', 'on_hold', 'inactive', 'blacklisted'];

const blank: ClientInput = {
  nameEn: '', nameAr: '', crNumber: '', vatNumber: '',
  address: '', city: '', region: '', postalCode: '',
  primaryContactName: '', primaryContactPhone: '', primaryContactEmail: '',
  financeContactName: '', financeContactPhone: '', financeContactEmail: '',
  paymentTermsDays: 30,
  creditLimitSar: null,
  preferredPaymentMethod: '',
  clientBankName: '', clientBankIban: '',
  status: 'active',
  notes: '',
};

export function ClientForm({ initial, onSubmit, onCancel, submitLabel }: ClientFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ClientInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        nameEn: initial.nameEn,
        nameAr: initial.nameAr ?? '',
        crNumber: initial.crNumber ?? '',
        vatNumber: initial.vatNumber ?? '',
        address: initial.address ?? '',
        city: initial.city ?? '',
        region: initial.region ?? '',
        postalCode: initial.postalCode ?? '',
        primaryContactName: initial.primaryContactName ?? '',
        primaryContactPhone: initial.primaryContactPhone ?? '',
        primaryContactEmail: initial.primaryContactEmail ?? '',
        financeContactName: initial.financeContactName ?? '',
        financeContactPhone: initial.financeContactPhone ?? '',
        financeContactEmail: initial.financeContactEmail ?? '',
        paymentTermsDays: initial.paymentTermsDays,
        creditLimitSar: initial.creditLimitSar != null ? Number(initial.creditLimitSar) : null,
        preferredPaymentMethod: initial.preferredPaymentMethod ?? '',
        clientBankName: initial.clientBankName ?? '',
        clientBankIban: initial.clientBankIban ?? '',
        status: initial.status,
        notes: initial.notes ?? '',
      });
    } else {
      setForm(blank);
    }
  }, [initial]);

  const update = <K extends keyof ClientInput>(key: K, value: ClientInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.nameEn?.trim()) { setError(t('clients.errors.nameRequired')); return; }
    if (form.primaryContactPhone && !/^\+\d{8,15}$/.test(form.primaryContactPhone)) {
      setError(t('clients.errors.phoneFormat')); return;
    }
    if (form.financeContactPhone && !/^\+\d{8,15}$/.test(form.financeContactPhone)) {
      setError(t('clients.errors.phoneFormat')); return;
    }

    const payload: ClientInput = {
      nameEn: form.nameEn.trim(),
      paymentTermsDays: form.paymentTermsDays ?? 30,
      status: form.status,
    };
    const optionals: (keyof ClientInput)[] = [
      'nameAr', 'crNumber', 'vatNumber', 'address', 'city', 'region', 'postalCode',
      'primaryContactName', 'primaryContactPhone', 'primaryContactEmail',
      'financeContactName', 'financeContactPhone', 'financeContactEmail',
      'preferredPaymentMethod', 'clientBankName', 'clientBankIban', 'notes',
    ];
    for (const key of optionals) {
      const v = (form as unknown as Record<string, unknown>)[key];
      if (typeof v === 'string' && v.trim()) (payload as unknown as Record<string, unknown>)[key] = v.trim();
    }
    if (form.creditLimitSar != null) payload.creditLimitSar = form.creditLimitSar;

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const IdentityTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid gap-3">
        <Field label={t('clients.field.nameEn')} required>
          <Input value={form.nameEn ?? ''} onChange={(e) => update('nameEn', e.target.value)} />
        </Field>
        <Field label={t('clients.field.nameAr')}>
          <Input dir="rtl" value={form.nameAr ?? ''} onChange={(e) => update('nameAr', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t('clients.field.crNumber')}>
          <Input dir="ltr" value={form.crNumber ?? ''} onChange={(e) => update('crNumber', e.target.value)} className="font-mono" placeholder="1010xxxxxx" />
        </Field>
        <Field label={t('clients.field.vatNumber')}>
          <Input dir="ltr" value={form.vatNumber ?? ''} onChange={(e) => update('vatNumber', e.target.value)} className="font-mono" placeholder="3000xxxxxxxxx03" />
        </Field>
      </div>
      <Field label={t('clients.field.status')}>
        <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as ClientStatus)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`clients.status.${s}`)}</option>)}
        </Select>
      </Field>
    </div>
  );

  const AddressTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <Field label={t('clients.field.address')}>
        <Input value={form.address ?? ''} onChange={(e) => update('address', e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label={t('clients.field.city')}>
          <Input value={form.city ?? ''} onChange={(e) => update('city', e.target.value)} />
        </Field>
        <Field label={t('clients.field.region')}>
          <Input value={form.region ?? ''} onChange={(e) => update('region', e.target.value)} placeholder="Eastern, Riyadh, Mecca..." />
        </Field>
        <Field label={t('clients.field.postalCode')}>
          <Input dir="ltr" value={form.postalCode ?? ''} onChange={(e) => update('postalCode', e.target.value)} className="font-mono" />
        </Field>
      </div>
    </div>
  );

  const ContactsTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t('clients.section.primaryContact')}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('clients.field.contactName')}>
            <Input value={form.primaryContactName ?? ''} onChange={(e) => update('primaryContactName', e.target.value)} />
          </Field>
          <Field label={t('clients.field.contactPhone')}>
            <Input dir="ltr" type="tel" value={form.primaryContactPhone ?? ''} onChange={(e) => update('primaryContactPhone', e.target.value)} className="font-mono" placeholder="+966500000000" />
          </Field>
          <Field label={t('clients.field.contactEmail')} className="md:col-span-2">
            <Input dir="ltr" type="email" value={form.primaryContactEmail ?? ''} onChange={(e) => update('primaryContactEmail', e.target.value)} />
          </Field>
        </div>
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t('clients.section.financeContact')}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('clients.field.contactName')}>
            <Input value={form.financeContactName ?? ''} onChange={(e) => update('financeContactName', e.target.value)} />
          </Field>
          <Field label={t('clients.field.contactPhone')}>
            <Input dir="ltr" type="tel" value={form.financeContactPhone ?? ''} onChange={(e) => update('financeContactPhone', e.target.value)} className="font-mono" placeholder="+966500000000" />
          </Field>
          <Field label={t('clients.field.contactEmail')} className="md:col-span-2">
            <Input dir="ltr" type="email" value={form.financeContactEmail ?? ''} onChange={(e) => update('financeContactEmail', e.target.value)} />
          </Field>
        </div>
      </fieldset>
    </div>
  );

  const FinanceTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t('clients.field.paymentTerms')}>
          <Input type="number" min={1} dir="ltr" value={form.paymentTermsDays ?? 30} onChange={(e) => update('paymentTermsDays', Number(e.target.value) || 30)} className="font-mono" />
          <p className="text-[11px] text-muted-foreground mt-1">{t('clients.help.paymentTerms')}</p>
        </Field>
        <Field label={t('clients.field.creditLimit')}>
          <Input type="number" step="0.01" dir="ltr" value={form.creditLimitSar ?? ''} onChange={(e) => update('creditLimitSar', e.target.value ? Number(e.target.value) : null)} className="font-mono" placeholder="100000" />
        </Field>
        <Field label={t('clients.field.preferredPaymentMethod')}>
          <Input value={form.preferredPaymentMethod ?? ''} onChange={(e) => update('preferredPaymentMethod', e.target.value)} placeholder="bank_transfer, cheque, cash" />
        </Field>
      </div>
      <fieldset className="space-y-3 pt-3 border-t border-border">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t('clients.section.banking')}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('clients.field.bankName')}>
            <Input value={form.clientBankName ?? ''} onChange={(e) => update('clientBankName', e.target.value)} />
          </Field>
          <Field label={t('clients.field.iban')}>
            <Input dir="ltr" value={form.clientBankIban ?? ''} onChange={(e) => update('clientBankIban', e.target.value.toUpperCase())} className="font-mono uppercase" placeholder="SA0000000000000000000000" />
          </Field>
        </div>
      </fieldset>
      <Field label={t('clients.field.notes')}>
        <textarea value={form.notes ?? ''} onChange={(e) => update('notes', e.target.value)} rows={3}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      </Field>
    </div>
  );

  const tabs = [
    { key: 'identity', label: <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" />{t('clients.tab.identity')}</span>, content: IdentityTab },
    { key: 'address',  label: <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{t('clients.tab.address')}</span>, content: AddressTab },
    { key: 'contacts', label: <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" />{t('clients.tab.contacts')}</span>, content: ContactsTab },
    { key: 'finance',  label: <span className="inline-flex items-center gap-1.5"><CreditCard className="h-4 w-4" />{t('clients.tab.finance')}</span>, content: FinanceTab },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <Tabs tabs={tabs} defaultKey="identity" />
      <div className="shrink-0 border-t border-border bg-muted/30">
        {error && (
          <p className="text-sm text-destructive p-3 mx-6 my-3 rounded bg-destructive/10 border border-destructive/30 flex items-start gap-2" role="alert">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>{t('common.cancel')}</Button>
          )}
          <Button type="submit" variant="gold" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel ?? t('common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {children}
    </select>
  );
}
